import { get } from 'lodash-es';
import { useCallback } from 'react';
import {
  useEntityDetailsDataStore,
  useSetEntityData,
} from '@web-config-app/core-react';

/**
 * Connects a localized field directly to the entity data store allowing
 * getting and setting localized values for a specific path and locale.
 *
 * Note: The default locale does NOT use this hook, as it is handled by
 * the `EntityForm` component directly.
 */

export interface UseLocalizedFieldProps {
  /**
   * The path to the localized field in the entity data, relative to
   * `domainEntityAttributes` inside the locale's data object
   */
  path: string;
  /**
   * The locale to get/set the localized value in.
   */
  locale: string;
}

export const useLocalizedField = ({ path, locale }: UseLocalizedFieldProps) => {
  const setEntityData = useSetEntityData();
  // using `useEntityDetailsDataStore` instead of `useEntityData` allows us to pass a selector, which will give better
  // performance and avoid unnecessary re-renders in consumers of this hook.
  const data = useEntityDetailsDataStore((state) => {
    const localeData =
      state.entityData?.attributes?.translations?.[locale]
        ?.domainEntityAttributes || {};
    return get(localeData, path);
  });

  const setData = useCallback(
    (newData: any) => {
      setEntityData(newData, `domainEntityAttributes.${path}`, locale);
    },
    [path, locale, setEntityData],
  );

  return {
    data,
    setData,
  };
};
